import React, { useState, useEffect } from "react";
import { Form, Button, Table, Spinner, InputGroup, FormControl } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose, faEdit, faPlus, faRefresh, faTrash } from "@fortawesome/free-solid-svg-icons";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
//import any = jasmine.any;

interface Item {
    [key: string]: any;
}

interface Column {
    key: string;
    class?: string;
    label: string;
    filterType?: "text" | "date" | "select" | "period";
    options?: { value: string; description: string }[];
    filterable?: boolean;
    render?: (item: Item) => React.ReactNode;
}

interface DefaultGridProps<T extends Item> {
    title?: string;
    columns: any;
    items: T[];
    itemsPerPage?: number;
    loading?: boolean;
    onEdit?: (item: T) => void;
    onDelete?: (item: T) => void;
    onAdd?: () => void;
    filters: Record<string, any>;
    handleFilterChange: (key: string, value: any) => void;
    onReload?: () => void;
    onClearFilters?: () => void;
    onEditInline?: (editedItem: any) => void;
}

const DefaultGrid = <T extends Item>({
                                         title,
                                         columns,
                                         items,
                                         itemsPerPage: initialItemsPerPage = 10,
                                         loading = false,
                                         onEdit,
                                         onDelete,
                                         onAdd,
                                         onReload,
                                         onClearFilters,
                                         filters,
                                         handleFilterChange,
                                     }: DefaultGridProps<T>) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(initialItemsPerPage);
    const [filteredItems, setFilteredItems] = useState<T[]>([]);

    useEffect(() => {
        applyFilters();
    }, [filters, items]);

    const applyFilters = () => {
        let filtered = [...items];
        Object.keys(filters).forEach((key) => {
            if (filters[key]) {
                filtered = filtered.filter((item) => {
                    // Lógica especial para o campo de CNPJ
                    if (key === "cnpj") {
                        const itemValue = item[key]?.toString().replace(/[\.\-\/]/g, ""); // Remove máscara
                        const filterValue = filters[key]?.toString().replace(/[\.\-\/]/g, ""); // Remove máscara do filtro
                        return itemValue.includes(filterValue);
                    }
                    // Lógica padrão para outros campos
                    return item[key]?.toString().toLowerCase().includes(filters[key].toLowerCase());
                });
            }
        });
        setFilteredItems(filtered);
        setCurrentPage(1);
    };

    const handleFilterInputChange = (key: string, value: string) => {
        const newFilters = { ...filters, [key]: value || undefined };
        handleFilterChange(key, value);
        applyFilters();
    };

    const paginatedItems = filteredItems.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const totalPages = Math.ceil(filteredItems.length / itemsPerPage);

    return (
        <div className="card">
            <div className="card-body">
                <div className="d-flex flex-column">
                    {/* Header with title, actions */}
                    <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
                        <h4>{title}</h4>
                        <div className="d-flex align-items-center">
                            {/* Items per page selector */}
                            <div className="input-group input-group-sm me-2">
                                <Form.Select
                                    size="sm"
                                    value={itemsPerPage}
                                    onChange={(e) => {
                                        setItemsPerPage(parseInt(e.target.value, 10));
                                        setCurrentPage(1);
                                    }}
                                >
                                    <option value="10">10</option>
                                    <option value="25">25</option>
                                    <option value="50">50</option>
                                    <option value="100">100</option>
                                </Form.Select>
                            </div>

                            {onReload && (<Button
                                variant="outline-secondary"
                                size="sm"
                                title="Recarregar dados"
                                className="me-2"
                                onClick={onReload}
                            >
                                <FontAwesomeIcon icon={faRefresh as IconProp} />
                            </Button>)}
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                title="Limpar Filtros"
                                className="me-2"
                                onClick={() => {
                                    handleFilterInputChange('',''); // Reset filters via props
                                    onClearFilters?.();
                                }}
                            >
                                <FontAwesomeIcon icon={faClose as IconProp} />
                            </Button>
                            {onAdd && (
                                <Button variant="outline-secondary" size="sm" onClick={onAdd}>
                                    Adicionar&nbsp;<FontAwesomeIcon icon={faPlus as IconProp} />
                                </Button>
                            )}
                        </div>
                    </div>

                    {/* Table */}
                    <div style={{ overflowX: "auto", minHeight: "400px" }}>
                        <Table hover striped className="table-bordered">
                            <thead className="sticky-top bg-light" style={{ zIndex: 1 }}>
                            <tr>
                                {columns.map((column:any) => (
                                    <th key={column.key}>
                                        <div>{column.label}</div>
                                        {column.filterType === "text" && (
                                            <InputGroup>
                                                <FormControl
                                                    type="text"
                                                    placeholder={`Buscar por ${column.label}`}
                                                    value={filters[column.key] || ""}
                                                    onChange={(e) =>
                                                        handleFilterInputChange(column.key, e.target.value)
                                                    }
                                                />
                                            </InputGroup>
                                        )}
                                        {column.filterType === "date" && (
                                            <InputGroup>
                                                <FormControl
                                                    type="date"
                                                    placeholder={`Buscar por ${column.label}`}
                                                    value={filters[column.key] || ""}
                                                    onChange={(e) =>
                                                        handleFilterInputChange(column.key, e.target.value)
                                                    }
                                                />
                                            </InputGroup>
                                        )}
                                        {column.filterType === "select" && (
                                            <InputGroup>
                                                <FormControl
                                                    as="select"
                                                    value={filters[column.key] || ""}
                                                    onChange={(e) =>
                                                        handleFilterInputChange(column.key, e.target.value)
                                                    }
                                                >
                                                    <option value="">Filtrar por {column.label}</option>
                                                    {column.options?.map((option: any) => (
                                                        <option key={option.value} value={option.value}>
                                                            {option.description}
                                                        </option>
                                                    ))}
                                                </FormControl>
                                            </InputGroup>
                                        )}
                                        {column.filterType === "period" && (
                                            <div>
                                                <InputGroup>
                                                    <FormControl
                                                        type="date"
                                                        placeholder={`Início ${column.label}`}
                                                        value={filters[column.key]?.[0] || ""}
                                                        onChange={(e) =>
                                                            handleFilterChange(column.key, [
                                                                e.target.value,
                                                                filters[column.key]?.[1],
                                                            ])
                                                        }
                                                    />
                                                </InputGroup>
                                                <InputGroup>
                                                    <FormControl
                                                        type="date"
                                                        placeholder={`Fim ${column.label}`}
                                                        value={filters[column.key]?.[1] || ""}
                                                        onChange={(e) =>
                                                            handleFilterChange(column.key, [
                                                                filters[column.key]?.[0],
                                                                e.target.value,
                                                            ])
                                                        }
                                                    />
                                                </InputGroup>
                                            </div>
                                        )}
                                    </th>
                                ))}
                            </tr>
                            </thead>
                            <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={columns.length + 1} className="text-center">
                                        <Spinner animation="border" />
                                    </td>
                                </tr>
                            ) : paginatedItems.length > 0 ? (
                                paginatedItems.map((item, index) => (
                                    <tr key={index}>
                                        {columns.map((column:any) => (
                                            <td key={column.key}>
                                                {column.render ? column.render(item) : item[column.key]}
                                            </td>
                                        ))}
                                        {(onEdit || onDelete) && (
                                            <td className="text-center">
                                                <div className="d-flex justify-content-center gap-2">
                                                    {onEdit && (
                                                        <Button
                                                            variant="outline-secondary"
                                                            size="sm"
                                                            onClick={() => onEdit(item)}
                                                        >
                                                            <FontAwesomeIcon icon={faEdit as IconProp} />
                                                        </Button>
                                                    )}
                                                    {onDelete && (
                                                        <Button
                                                            variant="outline-danger"
                                                            size="sm"
                                                            onClick={() => onDelete(item)}
                                                        >
                                                            <FontAwesomeIcon icon={faTrash as IconProp} />
                                                        </Button>
                                                    )}
                                                </div>
                                            </td>
                                        )}
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={columns.length + 1} className="text-center">
                                        Nenhum registro encontrado
                                    </td>
                                </tr>
                            )}
                            </tbody>
                        </Table>
                    </div>

                    {/* Pagination */}
                    <div className="mt-3 d-flex justify-content-center">
                        <div className="pagination gap-1">
                            <Button
                                className="btn"
                                variant="outline-secondary"
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                            >
                                &lt;
                            </Button>

                            <Button
                                className={`btn ${currentPage === 1 ? "active" : ""}`}
                                variant="outline-secondary"
                                onClick={() => setCurrentPage(1)}
                            >
                                1
                            </Button>

                            {(() => {
                                const maxVisiblePages = 5;
                                const visiblePages = [];
                                const startPage = Math.max(
                                    Math.min(currentPage - 1, totalPages - (maxVisiblePages - 1)),
                                    2
                                );
                                const endPage = Math.min(startPage + (maxVisiblePages - 3), totalPages - 1);

                                for (let page = startPage; page <= endPage; page++) {
                                    visiblePages.push(
                                        <Button
                                            key={page}
                                            className={`btn ${page === currentPage ? "active" : ""}`}
                                            variant="outline-secondary"
                                            onClick={() => setCurrentPage(page)}
                                        >
                                            {page}
                                        </Button>
                                    );
                                }

                                return visiblePages;
                            })()}

                            {totalPages > 1 && (
                                <Button
                                    className={`btn ${currentPage === totalPages ? "active" : ""}`}
                                    variant="outline-secondary"
                                    onClick={() => setCurrentPage(totalPages)}
                                >
                                    {totalPages}
                                </Button>
                            )}

                            <Button
                                className="btn"
                                variant="outline-secondary"
                                disabled={currentPage === totalPages}
                                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                            >
                                &gt;
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DefaultGrid;